import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  MessageSquare, Send, CheckCircle, Clock, Search,
  AlertCircle, ThumbsUp, ThumbsDown, RefreshCw
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';

const FeedbackPage = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: 'general',
    subject: '',
    message: '',
    priority: 'normal',
    mpn: '',
  });

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const res = await api.get('/feedback/list');
      setFeedbackList(res.data.feedback || []);
    } catch { /* ok */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFeedback(); }, []);

  const handleSubmit = async () => {
    if (!form.subject || !form.message) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/feedback/submit', form);
      toast.success('Feedback submitted! Our team will review it shortly.');
      setForm({ type: 'general', subject: '', message: '', priority: 'normal', mpn: '' });
      fetchFeedback();
    } catch { toast.error('Failed to submit feedback'); }
    finally { setSubmitting(false); }
  };

  const statusBadge = (s) => ({
    open: <Badge className="bg-blue-100 text-blue-800"><Clock className="h-3 w-3 mr-1" /> Open</Badge>,
    reviewed: <Badge className="bg-yellow-100 text-yellow-800"><RefreshCw className="h-3 w-3 mr-1" /> Reviewed</Badge>,
    resolved: <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Resolved</Badge>,
  }[s] || <Badge variant="outline">{s}</Badge>);

  return (
    <Layout title="Feedback" subtitle="Share product feedback or request a double-check on component analysis">
      <div className="space-y-6" data-testid="feedback-page">
        <Tabs defaultValue="submit" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-sm">
            <TabsTrigger value="submit">Submit Feedback</TabsTrigger>
            <TabsTrigger value="history" data-testid="feedback-history-tab">My Submissions</TabsTrigger>
          </TabsList>

          {/* Submit Tab */}
          <TabsContent value="submit" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-green-600" /> New Feedback
                      <InfoTip text="Submit product feedback, request a double-check on a specific component analysis, or report an issue." />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type</Label>
                        <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                          <SelectTrigger className="mt-1" data-testid="feedback-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Feedback</SelectItem>
                            <SelectItem value="double_check">Double Check Request</SelectItem>
                            <SelectItem value="bug_report">Bug Report</SelectItem>
                            <SelectItem value="feature_request">Feature Request</SelectItem>
                            <SelectItem value="data_issue">Data Issue</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Priority</Label>
                        <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                          <SelectTrigger className="mt-1" data-testid="feedback-priority"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {form.type === 'double_check' && (
                      <div>
                        <Label>Component MPN <InfoTip text="Enter the MPN you want us to double-check the analysis for" /></Label>
                        <Input value={form.mpn} onChange={e => setForm({ ...form, mpn: e.target.value })} placeholder="e.g., STM32F407VGT6" className="mt-1" data-testid="feedback-mpn" />
                      </div>
                    )}

                    <div>
                      <Label>Subject *</Label>
                      <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief description of your feedback" className="mt-1" data-testid="feedback-subject" />
                    </div>

                    <div>
                      <Label>Message *</Label>
                      <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Provide detailed feedback, steps to reproduce an issue, or context for your request..." className="mt-1" rows={5} data-testid="feedback-message" />
                    </div>

                    <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-feedback-btn">
                      {submitting ? 'Submitting...' : <><Send className="h-4 w-4 mr-2" /> Submit Feedback</>}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar tips */}
              <div className="space-y-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-5">
                    <h4 className="font-semibold text-blue-900 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Double Check</h4>
                    <p className="text-sm text-blue-800 mt-2">Not sure about a component analysis? Select "Double Check Request" and provide the MPN. Our team will review the data and get back to you.</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-5">
                    <h4 className="font-semibold text-green-900 flex items-center gap-2"><ThumbsUp className="h-4 w-4" /> What makes good feedback</h4>
                    <ul className="text-sm text-green-800 mt-2 space-y-1 list-disc list-inside">
                      <li>Specific MPN or BOM reference</li>
                      <li>Clear description of the issue</li>
                      <li>Expected vs actual behavior</li>
                      <li>Screenshots if applicable</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div>
                ) : feedbackList.length === 0 ? (
                  <div className="text-center py-12 text-gray-500" data-testid="no-feedback-message">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No feedback submitted yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {feedbackList.map((fb, idx) => (
                      <div key={idx} className="p-4 border border-gray-200 rounded-lg" data-testid={`feedback-item-${idx}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="capitalize text-xs">{fb.type?.replace('_', ' ')}</Badge>
                              {statusBadge(fb.status)}
                              {fb.priority === 'high' || fb.priority === 'urgent' ? <Badge className="bg-red-100 text-red-800 text-xs">{fb.priority}</Badge> : null}
                            </div>
                            <h4 className="font-semibold text-gray-900">{fb.subject}</h4>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{fb.message}</p>
                            {fb.mpn && <p className="text-xs text-gray-500 mt-1">MPN: <span className="font-mono">{fb.mpn}</span></p>}
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0">{new Date(fb.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default FeedbackPage;
